// The store platforms an offer can be sold on, and how to tell whether a given
// offer is sold on each.
//
// This mirrors ACE's availability rule, which is what actually decides whether
// a plan renders. Keep in sync with:
//   apps/ace/internal/offercatalog/service.go  storeProductFor + GetOffersByCodes
//   apps/ace/internal/service/offer.go         StorePlatform constants
//   apps/ace/.../schema.graphql                enum StorePlatform
//
// ACE's rule, restated:
//   an offer is available on a platform when it is ACTIVE, and either the
//   platform is WEB (web checkout needs no store product) or Cleeng carries a
//   non-empty in-app product id for that store.
//
// This matters because ACE deliberately does not fail on an offer a store
// doesn't sell — it skips the plan (see the plans resolver). A merchandiser
// would otherwise only discover the gap by opening the app on that device.

export interface StorePlatform {
  /** The ACE StorePlatform enum value. */
  code: string
  /** Short label for the availability row. */
  label: string
  /**
   * The key inside Cleeng's `appStoreProductIds` object, or null for WEB —
   * which sells any active offer and carries no store product at all.
   */
  cleengKey: string | null
}

export const STORE_PLATFORMS: readonly StorePlatform[] = [
  { code: 'WEB', label: 'Web', cleengKey: null },
  { code: 'APPLE_APP_STORE', label: 'Apple', cleengKey: 'apple' },
  { code: 'GOOGLE_PLAY', label: 'Google Play', cleengKey: 'android' },
  { code: 'ROKU', label: 'Roku', cleengKey: 'roku' },
  { code: 'AMAZON_APPSTORE', label: 'Amazon', cleengKey: 'amazon' },
  { code: 'SAMSUNG_TV', label: 'Samsung', cleengKey: 'samsung' },
  { code: 'VIZIO', label: 'Vizio', cleengKey: 'vizio' },
]

/** The offer fields this module needs — a structural subset of CleengOffer, so
 * callers can pass the picker's offers straight in. */
export interface OfferAvailabilityInput {
  active: boolean
  app_store_product_ids?: Record<string, string> | null
}

/**
 * Whether ACE will serve this offer on this platform. An inactive offer is
 * false everywhere, WEB is true for any active offer, and each native store
 * needs a non-empty product id.
 */
export const offerSellsOn = (
  offer: OfferAvailabilityInput,
  platform: StorePlatform
): boolean => {
  if (!offer.active) return false
  if (platform.cleengKey === null) return true
  return Boolean((offer.app_store_product_ids?.[platform.cleengKey] ?? '').trim())
}

// The plan name and period suffix ACE derives from a billing cycle when the
// widget authors neither. The editor shows these as placeholders, so a
// merchandiser sees the text the plan will actually use without storing it.
// Storing it would freeze the text: swap the offer for one with a different
// cycle later and the stored name stays wrong, while a blank one self-heals.
//
// Keep in sync with DerivePlanLabels in apps/ace/internal/service/models.go.
// Returns null when a cycle cannot name a plan (a one-time offer, or a unit ACE
// does not map) — the same case where ACE drops the plan.
const PERIOD_NAMES: Record<string, { adjective: string; singular: string; plural: string }> = {
  DAY: { adjective: 'DAILY', singular: 'day', plural: 'days' },
  WEEK: { adjective: 'WEEKLY', singular: 'week', plural: 'weeks' },
  MONTH: { adjective: 'MONTHLY', singular: 'month', plural: 'months' },
  YEAR: { adjective: 'ANNUAL', singular: 'year', plural: 'years' },
  SEASON: { adjective: 'SEASONAL', singular: 'season', plural: 'seasons' },
}

export interface BillingCycleInput {
  billing_period_unit?: string | null
  billing_period_count?: number | null
}

export const derivePlanLabels = (
  offer: BillingCycleInput
): { name: string; periodLabel: string } | null => {
  const count = offer.billing_period_count ?? 0
  const words = PERIOD_NAMES[(offer.billing_period_unit ?? '').toUpperCase()]
  if (!words || count < 1) return null
  if (count === 1) {
    return { name: words.adjective, periodLabel: `/ ${words.singular}` }
  }
  return {
    name: `EVERY ${count} ${words.plural.toUpperCase()}`,
    periodLabel: `/ ${count} ${words.plural}`,
  }
}
